import axios from 'axios';
import { normalizeSelectValue } from '@/lib/listingAttributes';

const DEFAULT_API_BASE_URL = 'https://dog-marketplace.onrender.com';

function cleanConfiguredValue(value) {
  const normalized = String(value || '').trim();
  return normalized && !normalized.startsWith('%') ? normalized : '';
}

const runtimeConfig = typeof window !== 'undefined' ? window : {};
const configuredApiBase =
  cleanConfiguredValue(runtimeConfig.__DOGGOHUB_API_BASE__) ||
  cleanConfiguredValue(process.env.REACT_APP_BACKEND_URL) ||
  DEFAULT_API_BASE_URL ||
  '';

export const API_BASE_URL = String(configuredApiBase).replace(/\/+$/, '');
export const API_URL = `${API_BASE_URL}/api`;

function parsePrice(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    const numeric = Number(value.replace(/[^0-9.]/g, ''));
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  if (value && typeof value === 'object') {
    if (typeof value.value === 'number') return Number.isFinite(value.value) ? value.value : undefined;
    if (typeof value.text === 'string') return parsePrice(value.text);
  }
  return undefined;
}

function textFromValue(value, fallback = '') {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (value && typeof value === 'object') {
    return value.text || value.name || value.city || value.province || value.state || fallback;
  }
  return fallback;
}

function normalizeLocation(location) {
  if (!location || typeof location !== 'object') return textFromValue(location);
  return location.text || [location.city, location.province || location.state, location.country].filter(Boolean).join(', ');
}

function normalizeImageRef(image) {
  if (!image) return '';
  if (typeof image === 'string') return image;
  if (typeof image === 'object') {
    return image.url || image.contentUrl || image.path || image.storage_path || '';
  }
  return '';
}

function normalizeImages(images) {
  if (!Array.isArray(images)) return [];
  return images.map(normalizeImageRef).filter(Boolean);
}

function firstTextValue(...values) {
  for (const value of values) {
    const text = textFromValue(value).trim();
    if (text) return text;
  }
  return '';
}

function normalizeExternalUrl(listing) {
  return firstTextValue(
    listing.external_url,
    listing.externalUrl,
    listing.source_url,
    listing.sourceUrl,
    listing.original_url,
    listing.originalUrl,
    listing.url,
    listing.canonicalUrl,
    listing.source?.url,
    listing.source?.website
  );
}

function sourceNameFromUrl(url) {
  if (!url || typeof window === 'undefined') return '';
  try {
    return new URL(url, window.location.origin).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function normalizeListing(listing) {
  if (!listing || typeof listing !== 'object') return listing;

  const listingId = listing.listing_id || listing.listingId || listing.externalId || listing.listingKey || listing._id;
  const breed = listing.breed || listing.dog?.breed || 'Unknown breed';
  const age = listing.age || listing.dog?.age || listing.postedDateText || '';
  const price = parsePrice(listing.price);
  const location = normalizeLocation(listing.location);
  const createdAt = listing.created_at || listing.createdAt || listing.scrapedAt || listing.updatedAt || '';
  const description = listing.description || listing.rawText || '';
  const title = listing.title || [breed, age].filter(Boolean).join(' - ') || 'Dog listing';
  const contact = listing.contact || listing.seller?.contact || {};
  const contactEmail = firstTextValue(listing.contact_email, listing.contactEmail, contact.email, listing.seller?.email, listing.user_email);
  const contactPhone = firstTextValue(
    listing.contact_phone,
    listing.contactPhone,
    listing.seller_phone,
    listing.sellerPhone,
    listing.phone,
    listing.phone_number,
    listing.phoneNumber,
    contact.phone,
    contact.phone_number,
    listing.seller?.phone,
    listing.seller?.phone_number
  );
  const externalUrl = normalizeExternalUrl(listing);
  const sourceName = firstTextValue(listing.source_name, listing.sourceName, listing.source?.name, sourceNameFromUrl(externalUrl));
  const gender = normalizeSelectValue(listing.gender || listing.dog?.gender);
  const hypoallergenic = normalizeSelectValue(
    listing.hypoallergenic ?? listing.is_hypoallergenic ?? listing.dog?.hypoallergenic ?? listing.dog?.is_hypoallergenic
  );

  return {
    ...listing,
    listing_id: String(listingId || ''),
    title,
    breed,
    age,
    price,
    location,
    description,
    size: listing.size || listing.dog?.size || '',
    gender,
    hypoallergenic,
    vaccination_status: listing.vaccination_status || 'unknown',
    status: listing.status || 'active',
    images: normalizeImages(listing.images),
    created_at: createdAt,
    updated_at: listing.updated_at || listing.updatedAt || createdAt,
    user_name: listing.user_name || listing.seller?.name || listing.source?.name || 'Seller',
    user_email: listing.user_email || listing.seller?.email || contactEmail,
    contact_email: contactEmail,
    contact_phone: contactPhone,
    external_url: externalUrl,
    source_name: sourceName,
    is_scraped: Boolean(listing.is_scraped || listing.scrapedAt || externalUrl),
  };
}

function normalizeListingResponse(response) {
  if (response?.data?.listings && Array.isArray(response.data.listings)) {
    response.data = {
      ...response.data,
      listings: response.data.listings.map(normalizeListing),
    };
  } else if (response?.data && typeof response.data === 'object') {
    response.data = normalizeListing(response.data);
  }
  return response;
}

function getHeaders() {
  const token = localStorage.getItem('auth_token');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export const api = {
  // Listings
  getListings: (params) => axios.get(`${API_URL}/listings`, { params }).then(normalizeListingResponse),
  getFeaturedListings: () => axios.get(`${API_URL}/listings/featured`).then(normalizeListingResponse),
  getListing: (id) => axios.get(`${API_URL}/listings/${id}`).then(normalizeListingResponse),
  getSiteStats: () => axios.get(`${API_URL}/stats`),
  createListing: (data) => axios.post(`${API_URL}/listings`, data, { headers: getHeaders(), withCredentials: true }),
  updateListing: (id, data) => axios.put(`${API_URL}/listings/${id}`, data, { headers: getHeaders(), withCredentials: true }),
  deleteListing: (id) => axios.delete(`${API_URL}/listings/${id}`, { headers: getHeaders(), withCredentials: true }),
  getMyListings: () => axios.get(`${API_URL}/my-listings`, { headers: getHeaders(), withCredentials: true }).then(normalizeListingResponse),

  // Upload
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/upload`, formData, {
      headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      withCredentials: true
    });
  },
  getFileUrl: (path) => {
    const normalizedPath = normalizeImageRef(path);
    if (!normalizedPath) return '';
    if (/^(https?:|data:|blob:)/i.test(normalizedPath)) return normalizedPath;
    return `${API_URL}/files/${String(normalizedPath).replace(/^\/+/, '')}`;
  },

  // Breeds
  getBreeds: (params) => axios.get(`${API_URL}/breeds`, { params }),
  getBreed: (slug) => axios.get(`${API_URL}/breeds/${slug}`),
  getBreedNames: () => axios.get(`${API_URL}/breeds/names`),

  // Favorites
  getFavorites: () => axios.get(`${API_URL}/favorites`, { headers: getHeaders(), withCredentials: true }).then(normalizeListingResponse),
  getFavoriteIds: () => axios.get(`${API_URL}/favorites/ids`, { headers: getHeaders(), withCredentials: true }),
  addFavorite: (id) => axios.post(`${API_URL}/favorites/${id}`, {}, { headers: getHeaders(), withCredentials: true }),
  removeFavorite: (id) => axios.delete(`${API_URL}/favorites/${id}`, { headers: getHeaders(), withCredentials: true }),

  // Messages
  sendMessage: (data) => axios.post(`${API_URL}/messages`, data, { headers: getHeaders(), withCredentials: true }),
  getConversations: () => axios.get(`${API_URL}/messages`, { headers: getHeaders(), withCredentials: true }),
  getConversation: (listingId, userId) => axios.get(`${API_URL}/messages/${listingId}/${userId}`, { headers: getHeaders(), withCredentials: true }),

  // Reviews
  createReview: (data) => axios.post(`${API_URL}/reviews`, data, { headers: getHeaders(), withCredentials: true }),
  getSellerReviews: (sellerId) => axios.get(`${API_URL}/reviews/${sellerId}`),

  // Matcher
  submitMatcher: (data) => axios.post(`${API_URL}/matcher`, data),

  // Blog
  getBlogPosts: (params) => axios.get(`${API_URL}/blog`, { params }),
  getBlogPost: (slug) => axios.get(`${API_URL}/blog/${slug}`),
};
